import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  Users, Wallet, Banknote, TrendingDown, Clock3, Landmark, Hourglass,
  Building2, RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';

import usePayrollEntity from '../../../utils/usePayrollEntity';
import { fetchPayrollEntityList } from '../../../store/slices/payrollSlice';
import payRollService from '../../../services/PayrollService';
import { fmtRs } from '../../../utils/payrollFormat';
import { StatGrid, ChartCard } from '../../../components/dashboard';



const FIELD =
  'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';
const tooltipStyle = { borderRadius: 8, fontSize: 12, border: '1px solid #e5e7eb' };

/* Compact money for KPI tiles — "Rs. 2.85M" instead of "Rs. 2,845,300.00". */
const fmtCompactRs = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `Rs. ${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `Rs. ${(v / 1_000).toFixed(1)}K`;
  return fmtRs(v);
};

const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const isFalseFlag = (v) => ['false', '0', 'n', 'no'].includes(String(v ?? '').trim().toLowerCase());

const resultSetOf = (res) => {
  const rs = res?.data?.ResultSet ?? res?.ResultSet ?? [];
  return Array.isArray(rs) ? rs : [];
};

/* Current month's period, else the most recent one. */
const pickDefaultPeriod = (periods) => {
  if (!periods.length) return null;
  const now = new Date();
  const current = periods.find(
    (p) => Number(p.month) === now.getMonth() + 1 && Number(p.year) === now.getFullYear()
  );
  if (current) return current;
  return [...periods].sort((a, b) => Number(b.id) - Number(a.id))[0];
};

const DONUT_COLORS = ['#059669', '#dc2626', '#2563eb', '#d97706', '#7c3aed', '#0891b2', '#db2777'];

const DonutLegend = ({ data, money = false }) => (
  <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2 text-[11px] text-gray-600">
    {data.map((d, i) => (
      <span key={d.name} className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
        {d.name} <span className="font-semibold text-gray-800">{money ? fmtRs(d.value) : d.value}</span>
      </span>
    ))}
  </div>
);

const Donut = ({ data, money = false }) => (
  <>
    <ResponsiveContainer width="100%" height={190}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
          {data.map((_, i) => (
            <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [money ? fmtRs(v) : v, n]} />
      </PieChart>
    </ResponsiveContainer>
    <DonutLegend data={data} money={money} />
  </>
);

/* ── Page ────────────────────────────────────────────────────────────────── */
const PayrollDashboard = () => {
  const dispatch = useDispatch();

  const { list: periods, loading: periodsLoading } = usePayrollEntity('payrollPeriods');
  const { list: employeeSalaries } = usePayrollEntity('employeeSalary');
  const { list: overtime, loading: otLoading } = usePayrollEntity('overtimeEntry');
  const { list: batches, loading: batchesLoading } = usePayrollEntity('batchManagement');
  const { list: loans, loading: loansLoading } = usePayrollEntity('loanManagement');

  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [headers, setHeaders] = useState([]);
  const [headersLoading, setHeadersLoading] = useState(false);
  const [bankRows, setBankRows] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);

  // Default to the current month's period once periods arrive.
  useEffect(() => {
    if (!selectedPeriodId && periods.length) {
      const p = pickDefaultPeriod(periods);
      if (p) setSelectedPeriodId(String(p.id));
    }
  }, [periods, selectedPeriodId]);

  const selectedPeriod = useMemo(
    () => periods.find((p) => String(p.id) === String(selectedPeriodId)),
    [periods, selectedPeriodId]
  );

  /* ── Period-specific data (payroll headers + bank report) ────────────── */
  const loadPeriodData = useCallback(async (period) => {
    if (!period) return;

    setHeadersLoading(true);
    setBankLoading(true);

    const monthKey = `${period.year}-${String(period.month).padStart(2, '0')}`;

    const [headerRes, bankRes] = await Promise.allSettled([
      payRollService.getPayrollHeadersByPeriod(period.id),
      payRollService.GetBankReportDetailsByDate(monthKey),
    ]);

    if (headerRes.status === 'fulfilled') {
      setHeaders(resultSetOf(headerRes.value));
    } else {
      setHeaders([]);
      toast.error('Could not load payroll totals for this period.');
    }
    setHeadersLoading(false);

    if (bankRes.status === 'fulfilled') {
      setBankRows(resultSetOf(bankRes.value));
    } else {
      setBankRows([]);
      toast.error('Could not load bank transfer details for this period.');
    }
    setBankLoading(false);
  }, []);

  useEffect(() => {
    loadPeriodData(selectedPeriod);
  }, [selectedPeriod, loadPeriodData]);

  const refresh = () => {
    ['payrollPeriods', 'employeeSalary', 'overtimeEntry', 'batchManagement', 'loanManagement'].forEach((key) =>
      dispatch(fetchPayrollEntityList(key))
    );
    loadPeriodData(selectedPeriod);
  };

  /* ── Payroll totals (from payroll headers) ───────────────────────────── */
  const totals = useMemo(() => {
    const t = { gross: 0, deductions: 0, net: 0, empEpf: 0, erEpf: 0, erEtf: 0, paye: 0 };
    headers.forEach((h) => {
      t.gross += num(h.HPH_GROSS_EARNINGS ?? h.HPH_TOTAL_EARNINGS);
      t.deductions += num(h.HPH_TOTAL_DEDUCTIONS);
      t.net += num(h.HPH_NET_SALARY);
      t.empEpf += num(h.HPH_EMPLOYEE_EPF);
      t.erEpf += num(h.HPH_EMPLOYER_EPF);
      t.erEtf += num(h.HPH_EMPLOYER_ETF);
      t.paye += num(h.HPH_PAYE);
    });
    return t;
  }, [headers]);

  const breakdownDonut = useMemo(() => {
    const other = Math.max(0, totals.deductions - totals.empEpf - totals.paye);
    return [
      { name: 'Net Pay', value: totals.net },
      { name: 'Employee EPF', value: totals.empEpf },
      { name: 'PAYE', value: totals.paye },
      { name: 'Other Deductions', value: other },
    ].filter((d) => d.value > 0);
  }, [totals]);

  /* ── Top earners (header net pay + employee names) ───────────────────── */
  const nameByEmployeeId = useMemo(() => {
    const map = {};
    employeeSalaries.forEach((e) => {
      if (e.employeeId != null && !map[e.employeeId]) map[e.employeeId] = e;
    });
    return map;
  }, [employeeSalaries]);

  const topEarners = useMemo(
    () =>
      headers
        .map((h) => {
          const emp = nameByEmployeeId[h.HPH_EMPLOYEE_ID];
          return {
            id: h.HPH_PAYROLL_ID ?? h.HPH_EMPLOYEE_ID,
            name: h.HED_REPORT_NAME || emp?.name || `Employee #${h.HPH_EMPLOYEE_ID}`,
            empNo: h.HED_SERVICE_NO || emp?.empNo || '',
            net: num(h.HPH_NET_SALARY),
          };
        })
        .sort((a, b) => b.net - a.net)
        .slice(0, 5),
    [headers, nameByEmployeeId]
  );
  const maxNet = Math.max(1, ...topEarners.map((r) => r.net));

  /* ── Overtime (selected period) ──────────────────────────────────────── */
  const periodOvertime = useMemo(
    () =>
      overtime.filter(
        (o) => String(o.payrollPeriodId) === String(selectedPeriodId) && !isFalseFlag(o.isActive)
      ),
    [overtime, selectedPeriodId]
  );

  const otTotals = useMemo(
    () =>
      periodOvertime.reduce(
        (s, o) => ({ amount: s.amount + num(o.amount), hours: s.hours + num(o.hours) }),
        { amount: 0, hours: 0 }
      ),
    [periodOvertime]
  );

  const otByType = useMemo(() => {
    const buckets = {};
    periodOvertime.forEach((o) => {
      const key = o.otType || 'Other';
      if (!buckets[key]) buckets[key] = { type: key, amount: 0, hours: 0 };
      buckets[key].amount += num(o.amount);
      buckets[key].hours += num(o.hours);
    });
    return Object.values(buckets).sort((a, b) => b.amount - a.amount);
  }, [periodOvertime]);

  const otApprovalDonut = useMemo(() => {
    const approved = periodOvertime.filter((o) => o.isApproved).length;
    return [
      { name: 'Approved', value: approved },
      { name: 'Pending', value: periodOvertime.length - approved },
    ].filter((d) => d.value > 0);
  }, [periodOvertime]);

  /* ── Batches (selected period) ───────────────────────────────────────── */
  const periodBatches = useMemo(
    () => batches.filter((b) => String(b.periodId) === String(selectedPeriodId) && b.isActive !== false),
    [batches, selectedPeriodId]
  );
  const pendingBatchCount = periodBatches.filter((b) => b.status !== 'Authorized').length;

  const batchStatus = useMemo(() => {
    const buckets = {};
    periodBatches.forEach((b) => {
      const key = b.status || 'Unknown';
      buckets[key] = (buckets[key] || 0) + 1;
    });
    return Object.entries(buckets).map(([status, count]) => ({ status, count }));
  }, [periodBatches]);

  /* ── Loans (outstanding, all periods) ────────────────────────────────── */
  const activeLoans = useMemo(() => loans.filter((l) => !isFalseFlag(l.isActive)), [loans]);
  const totalLoanBalance = useMemo(() => activeLoans.reduce((s, l) => s + num(l.balance), 0), [activeLoans]);

  /* ── Bank transfer by bank (selected period) ─────────────────────────── */
  const bankByName = useMemo(() => {
    const buckets = {};
    bankRows.forEach((r) => {
      const key = r.HBR_Bank_Name || r.HBR_Destination_Bank || 'Unknown';
      buckets[key] = (buckets[key] || 0) + num(r.HBR_Amount);
    });
    return Object.entries(buckets)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [bankRows]);
  const bankTotal = bankByName.reduce((s, b) => s + b.value, 0);

  /* ── KPI cards ───────────────────────────────────────────────────────── */
  const periodLabel = selectedPeriod?.name || 'Selected period';
  const dash = (loading, value) => (loading ? '…' : value);

  const stats = [
    { label: 'Employees Paid', value: dash(headersLoading, headers.length), sub: periodLabel, icon: Users, tone: 'primary' },
    { label: 'Gross Payroll', value: dash(headersLoading, fmtCompactRs(totals.gross)), sub: 'Before deductions', icon: Wallet, tone: 'blue' },
    { label: 'Net Payroll', value: dash(headersLoading, fmtCompactRs(totals.net)), sub: 'After deductions', icon: Banknote, tone: 'emerald' },
    { label: 'Deductions', value: dash(headersLoading, fmtCompactRs(totals.deductions)), sub: 'EPF, PAYE & other', icon: TrendingDown, tone: 'red' },
    { label: 'Employer EPF + ETF', value: dash(headersLoading, fmtCompactRs(totals.erEpf + totals.erEtf)), sub: 'Employer contribution', icon: Building2, tone: 'indigo' },
    { label: 'Overtime Cost', value: dash(otLoading, fmtCompactRs(otTotals.amount)), sub: `${otTotals.hours.toFixed(1)} hrs this period`, icon: Clock3, tone: 'amber' },
    { label: 'Loan Balance', value: dash(loansLoading, fmtCompactRs(totalLoanBalance)), sub: `${activeLoans.length} active loan(s)`, icon: Landmark, tone: 'violet' },
    { label: 'Pending Batches', value: dash(batchesLoading, pendingBatchCount), sub: `of ${periodBatches.length} this period`, icon: Hourglass, tone: 'orange' },
  ];

  const loadingText = 'Loading…';

  return (
    <div className="p-6 space-y-4">
      {/* ── Filters ── */}
      <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <span className={LABEL}>Payroll Period</span>
            <select
              value={selectedPeriodId}
              onChange={(e) => setSelectedPeriodId(e.target.value)}
              className={FIELD}
              disabled={periodsLoading && !periods.length}
            >
              {!periods.length && <option value="">{periodsLoading ? 'Loading periods…' : 'No periods'}</option>}
              {periods.map((period) => (
                <option key={period.id} value={period.id}>
                  {period.code} - {period.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2 md:col-span-3">
            <button
              type="button"
              onClick={refresh}
              disabled={headersLoading || bankLoading}
              className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors disabled:opacity-60"
            >
              <RefreshCw size={14} className={headersLoading || bankLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI cards ── */}
      <StatGrid stats={stats} columns={8} />

      {/* ── Row 1: OT by type (span 2) + payroll breakdown + bank transfer ── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2">
          <ChartCard
            title="Overtime Cost by Type"
            subtitle={`Total: ${fmtRs(otTotals.amount)} · ${otTotals.hours.toFixed(1)} hrs`}
            empty={otLoading || otByType.length === 0}
            emptyText={otLoading ? loadingText : 'No overtime for this period'}
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={otByType} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="type" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n, item) => [`${fmtRs(v)} (${item.payload.hours.toFixed(1)} hrs)`, 'OT Cost']}
                />
                <Bar dataKey="amount" fill="#d97706" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard
          title="Payroll Breakdown"
          subtitle={`Gross: ${fmtRs(totals.gross)}`}
          empty={headersLoading || breakdownDonut.length === 0}
          emptyText={headersLoading ? loadingText : 'Payroll not calculated for this period'}
        >
          <Donut data={breakdownDonut} money />
        </ChartCard>

        <ChartCard
          title="Bank Transfer by Bank"
          subtitle={`Total: ${fmtRs(bankTotal)} · ${bankRows.length} transfer(s)`}
          empty={bankLoading || bankByName.length === 0}
          emptyText={bankLoading ? loadingText : 'No bank report for this period'}
        >
          <Donut data={bankByName} money />
        </ChartCard>
      </div>

      {/* ── Row 2: top earners + batch status + OT approval ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard
          title="Top Earners"
          subtitle="Highest net pay this period"
          empty={headersLoading || topEarners.length === 0}
          emptyText={headersLoading ? loadingText : 'Payroll not calculated for this period'}
        >
          <div className="space-y-3.5">
            {topEarners.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3">
                <span
                  className={`w-6 h-6 shrink-0 rounded-full text-white text-[11px] font-bold flex items-center justify-center ${
                    i < 3 ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  {i + 1}
                </span>
                <span
                  className={`w-24 shrink-0 text-xs truncate ${i < 3 ? 'text-primary-600 font-medium' : 'text-gray-600'}`}
                  title={r.empNo ? `${r.name} (${r.empNo})` : r.name}
                >
                  {r.name}
                </span>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full relative">
                  <div
                    className="absolute inset-y-0 left-0 bg-primary-500 rounded-full"
                    style={{ width: `${(r.net / maxNet) * 100}%` }}
                  />
                </div>
                <span className="w-24 text-right text-xs font-semibold text-gray-700 tabular-nums">
                  {fmtRs(r.net)}
                </span>
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard
          title="Batch Status"
          subtitle={`${periodBatches.length} batch(es) this period`}
          empty={batchesLoading || batchStatus.length === 0}
          emptyText={batchesLoading ? loadingText : 'No batches for this period'}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={batchStatus} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="status" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Batches']} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40}>
                {batchStatus.map((entry, i) => (
                  <Cell key={i} fill={entry.status === 'Authorized' ? '#059669' : '#d97706'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Overtime Approval"
          subtitle={`${periodOvertime.length} OT entr${periodOvertime.length === 1 ? 'y' : 'ies'} this period`}
          empty={otLoading || otApprovalDonut.length === 0}
          emptyText={otLoading ? loadingText : 'No overtime for this period'}
        >
          <Donut data={otApprovalDonut} />
        </ChartCard>
      </div>
    </div>
  );
};

export default PayrollDashboard;